import RootState from 'app-components/utils/redux-store/rootState';
import store from '../../utils/redux-store';
import { FEATURE_FLAGS_REDUCER_KEY, featureFlagsSelectors } from '../feature-flags';

import {
  ACCOUNTS_REDUCER_KEY,
  AccountClassification,
  AccountProps,
  AccountRecurringTxn,
  AccountType,
  AccountTypeNodeProps,
} from './accountsTypes';

// non-imported account lookup to prevent circular imports
const getAccountById = (state: Pick<RootState, ACCOUNTS_REDUCER_KEY> | undefined | null, id?: string | null) => {
  if (!id) return undefined;
  const lookupState = state || store?.getState();
  return lookupState?.[ACCOUNTS_REDUCER_KEY]?.resourcesById?.get(id);
};

// Retriever import (NOT SELECTORS)
export function getAccountString(id?: string | null, state?: Pick<RootState, ACCOUNTS_REDUCER_KEY> | null) {
  const account = getAccountById(state, id);
  if (account) {
    return account.name;
  }
  return '[Account Not Synced]';
}
export function getCurrencyString(id?: string | null, state?: Pick<RootState, ACCOUNTS_REDUCER_KEY> | null) {
  const account = getAccountById(state, id);
  if (account) {
    return account.currency;
  }
  return 'N/A';
}

// -------------------------------------------------------
// isLoanAccountFromId
//
export function isLoanAccountFromId(
  id: string | undefined | null,
  state?: Pick<RootState, ACCOUNTS_REDUCER_KEY> | null,
) {
  return isLoanAccount(getAccountById(state, id));
}

// -------------------------------------------------------
// isInvestmentAccountFromId
//
export function isInvestmentAccountFromId(
  id: string | undefined | null,
  state?: Pick<RootState, ACCOUNTS_REDUCER_KEY> | null,
) {
  return isInvestmentAccount(getAccountById(state, id));
}

// -------------------------------------------------------
// isBankAccountFromId
//
export function isBankAccountFromId(
  id: string | undefined | null,
  state?: Pick<RootState, ACCOUNTS_REDUCER_KEY> | null,
) {
  return isBankAccount(getAccountById(state, id));
}
// -------------------------------------------------------
// isActiveConnectedAccount
//
export function isActiveConnectedAccountFromId(
  id: string | undefined | null,
  state?: Pick<RootState, ACCOUNTS_REDUCER_KEY> | null,
) {
  return isAccountConnected(getAccountById(state, id));
}

// -------------------------------------------------------
// isBankAccount
//
export function isBankAccount(acct?: AccountProps) {
  return Boolean(acct && acct.type === 'BANK');
}

// -------------------------------------------------------
// isLoanAccount
//
export function isLoanAccount(acct?: AccountProps) {
  return Boolean(acct && acct.type === 'LOAN');
}

// -------------------------------------------------------
// isInvestmentAccount
//
export function isInvestmentAccount(acct?: AccountProps) {
  return Boolean(acct && acct.type === 'INVESTMENT');
}

export function isStripeAccount(acct?: AccountProps) {
  const channel = acct?.aggregators?.get(0)?.channel;
  return Boolean(channel === 'STRIPE_TEST' || channel === 'STRIPE');
}

// -------------------------------------------------------
// isAccountConnected
//
export const isAccountConnected = (account?: AccountProps) => account?.isConnected;

// A disconnected account will be defined as an account which belongs to an institution-login but is not connected.
// If it doesn't belong to an institution-login then it will be considered manual.
//
// Note: If you want to see whether an account is connected or not-connected regardless of whether it is considered
//       manual, use isAccountConnected(account) or !isAccountConnected(account).
//         !isAccountConnected(account) = isAccountDisconnected(account) || isAccountManual(account)
//
export const isAccountDisconnected = (account?: AccountProps, _institutionLogin?: unknown) =>
  account?.institutionLoginId && account.institutionLoginId !== '0' && !account.isConnected;

// A manual account will be defined as an account which belongs to no institution-login. If it belongs to an
// institution-login but is not connected, we will consider it disconnected account, not manual.
//
// Note: If you want to see whether an account is connected or not-connected regardless of whether it is considered
//       manual, use isAccountConnected(account) or !isAccountConnected(account).
//         !isAccountConnected(account) = isAccountDisconnected(account) || isAccountManual(account)
//
export const isAccountManual = (account?: AccountProps) =>
  !account?.institutionLoginId || account.institutionLoginId === '0';

export function transactionsNotSupportedForAccountId(
  accountId: string | undefined | null,
  hideConnectedLoanTransactions: boolean = false,
  state: Pick<RootState, typeof FEATURE_FLAGS_REDUCER_KEY | ACCOUNTS_REDUCER_KEY>,
) {
  const investmentTransactionsEnabled = featureFlagsSelectors.getFeatureFlag(state, 'investmentTransactions');
  if (!investmentTransactionsEnabled && isInvestmentAccountFromId(accountId, state)) return true;

  return Boolean(
    isLoanAccountFromId(accountId, state) &&
      hideConnectedLoanTransactions &&
      isActiveConnectedAccountFromId(accountId, state),
  );
}

// -------------------------------------------------------
// Convert the transaction setting to a number of days
// but of course, does not work for number of occurrences
//
export function getReminderSettingInDays(recurringTxn: AccountRecurringTxn) {
  if (recurringTxn) {
    switch (recurringTxn.unitType) {
      case 'MONTHS':
        // this is not really accurate  TODO: Use date calcs to be truly accurate
        return recurringTxn.value * 31;
      case 'OCCURRENCES':
        return -1 * recurringTxn.value; // should resolve to 0 or -1

      default:
        return recurringTxn.value || 0;
    }
  }
  return 0;
}
// -------------------------------------------------------
// Convert back, dependent upon dataset type
//
export function getReminderObjectFromDays(days: number, _isWindows = true) {
  if (days < 0) {
    return { unitType: 'OCCURRENCES', value: 1 };
  }
  if (days === 0) {
    return { unitType: 'OCCURRENCES', value: 0 };
  }
  /* MAC is now only sending DAYS
  if (!isWindows && days > 90) {
    return { unitType: 'MONTHS', value: Math.round(days/31) }
  }
  */
  return { unitType: 'DAYS', value: days };
}

export const isAccountNodeSelectable = (
  node: AccountTypeNodeProps,
  initialNode: AccountTypeNodeProps | undefined,
  businessEnabled: boolean,
) => {
  if (!node.selectable || (!businessEnabled && node.business)) {
    return false;
  }

  // If initial-node is set and not unknown, then here is where we can lock specific types. For example,
  // if account is initially set to type investment, then this can be used to make sure type cannot
  // be set to anything but investment types.
  //
  return (
    !initialNode ||
    initialNode.qcsType === 'UNKNOWN' ||
    !['INVESTMENT', 'PAYMENT'].includes(initialNode.qcsType) ||
    (initialNode.qcsType === 'INVESTMENT' && node.qcsType === 'INVESTMENT') ||
    (initialNode.qcsType === 'PAYMENT' && node.qcsType === 'PAYMENT')
  );
};

export const isAccountFiConnectable = (account: AccountProps) => {
  const { type } = account;
  return (
    type === 'BANK' ||
    type === 'CREDIT' ||
    type === 'INVESTMENT' ||
    type === 'LOAN' ||
    type === 'PAYMENT' ||
    type === 'UNKNOWN'
  );
};

export const balanceSignForType = (accountType: AccountType) => {
  let sign;
  switch (accountType) {
    case 'CREDIT':
    case 'OTHER_LIABILITY':
    case 'LOAN':
      sign = -1;
      break;
    default:
      sign = +1;
  }
  return sign;
};

export const isAccountInError = (account?: AccountProps) => {
  let isError = false;
  const aggStatus = account?.aggregators?.get(0)?.aggStatus;

  // TODO: A number of statuses are OK and should not result in an error
  if (aggStatus && aggStatus !== 'OK') {
    isError = true;
  }
  return {
    isError,
    errorStatus: aggStatus,
  };
};

export const isAccountInMigrationState = (account?: AccountProps) => {
  const aggStatus = account?.aggregators?.get(0)?.aggStatus;
  return aggStatus === 'CHANNEL_RESET_REQUIRED' || aggStatus === 'FI_MIGRATION';
};

export const formatFinalFour = (account?: AccountProps, useX?: boolean) => {
  const aggregator = account?.aggregators?.first();
  const final4Expected = aggregator?.accountNumberMasked?.replaceAll?.(/\D/g, '')?.replaceAll?.(/x|X|\*/gi, '') || null;
  const maskingItems = useX ? 'xxxx' : '••••';
  return final4Expected ? `${maskingItems}${final4Expected}` : '';
};

// these are the names we are currently blocking due to amcharts overlap
export const isInvalidName = (accountName: string) => {
  const invalidNames = ['total', 'isoMask', 'custom', 'category', 'txns', 'timeStamp'];
  return invalidNames.includes(accountName);
};

//-------------------------------------------------------------------------------------------------
// Business Related Selectors
//-------------------------------------------------------------------------------------------------

export const doesAccountSupportPaymentSrcs = (account?: AccountProps) =>
  account?.type === 'BANK' ||
  (account?.type === 'OTHER_ASSET' && (account?.subType === 'UNDEPOSITED_FUNDS' || account?.subType === 'UNKNOWN')) ||
  account?.type === 'CASH' ||
  account?.type === 'PAYMENT' ||
  (account?.type === 'OTHER_LIABILITY' && account?.subType === 'UNEARNED_INCOME');

// Asset accounts generally support refund sources. However, some types or subtypes are not applicable. Currently,
// will exclude REAL_ESTATE and VEHICLE accounts but should also exclude some investment subtype accounts.
//
export function doesAccountSupportRefundSrcs(account: AccountProps) {
  return (
    classifyAccountType(account.type) === 'ASSET' &&
    account.type !== 'ACCOUNTS_RECEIVABLE' &&
    account.type !== 'REAL_ESTATE' &&
    account.type !== 'VEHICLE'
  );
}

const SHOW_CHECK_NUMBER_ON_ACCOUNT_SUB_TYPES = new Set([
  // banking
  'CHECKING',
  'SAVINGS',
  'MONEY_MARKET',
  'OTHER_BANK',
  // investment
  'BROKERAGE',
  //payment
  'PAYMENT',
]);

export const hasCheckNumberField = (account?: AccountProps) =>
  !!account?.subType && SHOW_CHECK_NUMBER_ON_ACCOUNT_SUB_TYPES.has(account?.subType);

export function classifyAccountType(accountType: AccountType): AccountClassification {
  const assetTypes: AccountType[] = [
    'BANK',
    'CASH',
    'ASSET',
    'REAL_ESTATE',
    'VEHICLE',
    'OTHER_ASSET',
    'INVESTMENT',
    'GOAL',
    'ACCOUNTS_RECEIVABLE',
  ];

  const liabilityTypes: AccountType[] = ['CREDIT', 'LOAN', 'ACCOUNTS_PAYABLE', 'OTHER_LIABILITY', 'PAYMENT'];

  // const equityTypes: AccountType[] = [
  //   'RETAINED_INCOME'
  // ];

  if (assetTypes.includes(accountType)) return 'ASSET';
  if (liabilityTypes.includes(accountType)) return 'LIABILITY';
  // if (equityTypes.includes(accountType)) return 'EQUITY';

  return 'UNKNOWN';
}

export const isQBPAccount = (account: AccountProps): boolean =>
  (account.type === 'OTHER_LIABILITY' && account.subType === 'SALES_TAX') ||
  account.type === 'ACCOUNTS_RECEIVABLE' ||
  account.type === 'ACCOUNTS_PAYABLE' ||
  isStripeAccount(account);
